import {
  PlanDay,
  PlanDayKind,
  PlanResource,
  ResourceTiming,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

/** How the day's resource is presented: a skill unit to study, or a full official section. */
export type DailyResourceKind = 'khan' | 'practiceTest';

/** The single guaranteed daily resource for a plan day; null on days that carry no resource. */
export type DailyResource = Readonly<{
  kind: DailyResourceKind;
  url: string;
  /** Display label for the link; null when the plan payload carried no resolved resource. */
  label: string | null;
  /** Caveat line for the card (e.g. ACT skills studied via the SAT course); null when none. */
  note: string | null;
  /** The provider's own clock; null on study days and wherever none was resolved. */
  timing: ResourceTiming | null;
  test: TargetTest;
  section: string;
  category: string;
  /** Stable reporting-category code; null on timed days and when the engine resolved none. */
  categoryCode: string | null;
  /** Null on timed days and when the engine resolved no standard. */
  skillText: string | null;
}>;

// Floor links for a day that resolved no resource. Landing pages, not deep
// links, so they stay correct without knowing what the day wanted.
const KHAN_DIGITAL_SAT_URL = 'https://www.khanacademy.org/digital-sat';
const ACT_SCIENCE_PRACTICE_URL =
  'https://www.act.org/content/act/en/products-and-services/the-act/test-preparation/free-act-test-prep/act-online-test-sample-questions.html';
const SAT_PRACTICE_TEST_URL = 'https://bluebook.collegeboard.org';
const ACT_PRACTICE_TEST_URL =
  'https://www.act.org/content/act/en/products-and-services/the-act/test-preparation.html';

const logMissingResource = (dayKind: PlanDayKind, test: TargetTest, section: string): void => {
  console.error('[test-booster] day resolved no resource; using the floor link', {
    dayKind,
    test,
    section,
  });
};

const isActScience = (test: TargetTest, section: string): boolean =>
  test === 'act' && section === 'Science';

const studyResourceUrl = (test: TargetTest, section: string): string =>
  isActScience(test, section) ? ACT_SCIENCE_PRACTICE_URL : KHAN_DIGITAL_SAT_URL;

const practiceTestUrl = (test: TargetTest): string =>
  test === 'act' ? ACT_PRACTICE_TEST_URL : SAT_PRACTICE_TEST_URL;

/**
 * Resolve the day's resource card from the plan payload. Study days get the skill's resolved
 * study unit; timed days get the official full-length practice section.
 * Rest, off, and test days carry no resource, so they return null.
 */
export const resourceForDay = (day: PlanDay, test: TargetTest): DailyResource | null => {
  if (day.kind === 'timed') {
    const entry = day.entries[0];
    const section = entry?.section ?? '';
    const resource: PlanResource | null = entry?.resource ?? null;

    if (!section) return null;

    if (!resource) logMissingResource('timed', test, section);

    return {
      kind: 'practiceTest',
      url: resource?.url ?? practiceTestUrl(test),
      label: resource?.label ?? null,
      note: resource?.note ?? null,
      timing: resource?.timing ?? null,
      test,
      section,
      category: section,
      categoryCode: null,
      skillText: null,
    };
  }

  if (day.kind !== 'study') return null;

  const target = day.targets[0];

  if (!target) return null;

  if (!target.resource) logMissingResource('study', test, target.section);

  return {
    kind: 'khan',
    url: target.resource?.url ?? studyResourceUrl(test, target.section),
    label: target.resource?.label ?? null,
    note: target.resource?.note ?? null,
    timing: null,
    test,
    section: target.section,
    category: target.category,
    categoryCode: target.categoryCode,
    skillText: target.skillText,
  };
};
